import { AdminOnly } from "../../components/authFilter/AdminOnly";
import { Navigate } from "react-router";
import { useDeliveryZoneMode } from "../../hooks/useDeliveryZoneMode";
import { useBasicDeliveryDefaults } from "../../hooks/useBasicDeliveryDefaults";
import { useParentNavigation } from "../../hooks/useParentNavigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { Icon, SpinnerLegacy } from "../../components";
import { Alert } from "../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../components/modal/Dialog";
import { useCustomerZone } from "../../hooks/api/v2/customerZones/useCustomerZone";
import { useCustomerZonePostcodes } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodes";
import { useCustomerZoneDeliveryAreas } from "../../hooks/api/v2/customerZones/useCustomerZoneDeliveryAreas";
import { useCustomerZonePostcodesAdd } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesAdd";
import { useCustomerZonePostcodesRemove } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesRemove";
import { useCustomerZonePostcodesMove } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesMove";
import { useCustomerZonesUpdate } from "../../hooks/api/v2/customerZones/useCustomerZonesUpdate";
import { useCustomerZonesDelete } from "../../hooks/api/v2/customerZones/useCustomerZonesDelete";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import * as ROUTES from "../../constants/routes";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../components";
import { useFulfillmentTypeList } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useDeliveryAreaList } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { PostcodeMoveDialog } from "../../components/postcode/PostcodeMoveDialog";
import { EmptyZoneAfterMoveDialog } from "../../components/postcode/EmptyZoneAfterMoveDialog";
import { isPostcodeConflictError } from "../../utils/postcodeErrorHelper";
import {
  CustomerZoneGetResponseType,
  CustomerZonePostcodeAddResponseType,
  DeliveryAreaListResponseItemType,
  FulfillmentTypeListResponseType,
  SupplierGetResponseType,
  CustomerZoneUpdateRequestType,
} from "@sellar-io/api-v2/types";
import { useFocusedNavigation } from "~/hooks/useFocusedNavigation";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useSupplier } from "~/hooks/api/v2/supplier/useSupplier";
import { useUniqueDirectPostcodes } from "../customerZones/useUniqueDirectPostcodes";
import {
  capitalize,
  checkPostcodeValidCharacters,
  formatCurrency,
  getCoverageMismatches,
  normalizePostcodeComponent,
  pluralize,
} from "@sellar-io/common";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import {
  Anchor,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  InputTextArea,
  ListItemIconTile,
  SingleFieldDialog,
  SingleFieldDialogImage,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { useDeliveryAreaUpdate } from "~/hooks/api/v2/deliveryArea/useDeliveryAreaUpdate";
import { twMerge } from "tailwind-merge";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { expandPostcodeRange } from "../../utils/expandPostcodeRange";
import { groupPostcodes } from "./groupPostcodes";
import { DeliveryMethodPreviewSlideOut } from "./DeliveryMethodPreviewSlideOut";

type DeliveryAreaUpdateMutation = ReturnType<typeof useDeliveryAreaUpdate>;
type DeliveryAreaUpdateMutationsMap = Record<
  number,
  DeliveryAreaUpdateMutation | undefined
>;

export const DeliveryZone = () => {
  useFocusedNavigation();
  const mode = useDeliveryZoneMode();
  const defaults = useBasicDeliveryDefaults(mode.isBasicMode);

  const { business } = useSellarCurrentBusiness();
  const supplierId = business?.Supplier?.id;
  const deliveryZoneId = useParamAsNumber(
    ROUTES.DELIVERY_ZONE,
    "deliveryZoneId",
  );

  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  const { data: supplier } = useSupplier(supplierId ?? 0, {
    enabled: !!supplierId,
  });

  const { data: customerZone, isLoading } = useCustomerZone(
    deliveryZoneId ?? 0,
  );

  const customerZoneUpdate = useCustomerZonesUpdate(deliveryZoneId ?? 0);

  const drayDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "direct",
      },
    ],
    limit: 20,
  });

  const courierDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "courier",
      },
    ],
    limit: 20,
  });

  const palletDeliveryMethodsQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        modifier: "EQUALS",
        value: "haulier",
      },
    ],
    limit: 20,
  });

  const drayDeliveryMethods = useMemo(
    () => drayDeliveryMethodsQuery.data?.pages.flat() ?? [],
    [drayDeliveryMethodsQuery.data],
  );
  const courierDeliveryMethods = useMemo(
    () => courierDeliveryMethodsQuery.data?.pages.flat() ?? [],
    [courierDeliveryMethodsQuery.data],
  );
  const palletDeliveryMethods = useMemo(
    () => palletDeliveryMethodsQuery.data?.pages.flat() ?? [],
    [palletDeliveryMethodsQuery.data],
  );

  if (isLoading || mode.isLoading || (mode.isBasicMode && defaults.isLoading))
    return <SpinnerLegacy />;
  if (mode.isBasicMode && defaults.query.isError)
    return (
      <div className="p-4" role="alert">
        <p>Unable to load default delivery methods.</p>
        <Button
          onClick={() => {
            void defaults.query.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  if (
    mode.isBasicMode &&
    deliveryZoneId !== defaults.local?.id &&
    deliveryZoneId !== defaults.nationwide?.id
  )
    return <Navigate to={ROUTES.DELIVERY_METHODS} replace />;

  if (!customerZone) throw new Error("Customer zone not found");

  return (
    <DeliveryZoneView
      key={deliveryZoneId}
      isBasicMode={mode.isBasicMode}
      customerZone={customerZone}
      deliveryZoneId={deliveryZoneId}
      supplier={supplier}
      unbundlingEnabled={unbundlingEnabled}
      drayDeliveryMethods={drayDeliveryMethods}
      courierDeliveryMethods={courierDeliveryMethods}
      palletDeliveryMethods={palletDeliveryMethods}
      onSave={async (updatePayload) => {
        if (Object.keys(updatePayload).length > 0) {
          await customerZoneUpdate.mutateAsync(updatePayload);
        }
      }}
    />
  );
};

const DeliveryZoneView = ({
  isBasicMode,
  customerZone,
  deliveryZoneId,
  supplier,
  unbundlingEnabled,
  drayDeliveryMethods,
  courierDeliveryMethods,
  palletDeliveryMethods,
  onSave,
}: {
  isBasicMode: boolean;
  customerZone: CustomerZoneGetResponseType;
  deliveryZoneId: number;
  supplier: SupplierGetResponseType | undefined;
  unbundlingEnabled: boolean;
  drayDeliveryMethods: FulfillmentTypeListResponseType[number][];
  courierDeliveryMethods: FulfillmentTypeListResponseType[number][];
  palletDeliveryMethods: FulfillmentTypeListResponseType[number][];
  onSave: (updatePayload: CustomerZoneUpdateRequestType) => Promise<void>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToParent = useParentNavigation({
    fallback: isBasicMode ? ROUTES.DELIVERY_METHODS : ROUTES.DELIVERY_ZONES,
  });

  const flags = useRemoteFeatureFlags();
  const canRemoveNationwide =
    customerZone.type === "thirdParty" &&
    customerZone.default &&
    customerZone.systemDefinedType === null &&
    flags.isActive("unbundlingReleaseFinal");
  const removeZone = useCustomerZonesDelete(deliveryZoneId);
  const [removeNationwideActive, setRemoveNationwideActive] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [nameDraft, setNameDraft] = useState(customerZone.name ?? "");

  const savedDeliveryNotes =
    customerZone.deliveryNotes ??
    (customerZone.type === "direct"
      ? (supplier?.directDeliveryNotes ?? "")
      : (supplier?.deliveryNotes ?? ""));
  const [deliveryNotesDraft, setDeliveryNotesDraft] = useState<string | null>(
    null,
  );
  const [previewSlideOutActive, setPreviewSlideOutActive] = useState(false);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<FulfillmentTypeListResponseType[number]>();

  const [pendingIds, setPendingIds] = useState<{
    dray: number | null;
    courier: number | null;
    pallet: number | null;
  }>(() => ({
    dray: customerZone.DrayDeliveryMethodId ?? null,
    courier: customerZone.CourierDeliveryMethodId ?? null,
    pallet: customerZone.PalletDeliveryMethodId ?? null,
  }));

  const [showNoPostcodesAlert, setShowNoPostcodesAlert] = useState(false);

  const onClickClose = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveError(null);

    try {
      const updatePayload: CustomerZoneUpdateRequestType = {};

      if (!isBasicMode && nameDraft.trim() !== (customerZone.name ?? "")) {
        updatePayload.name = nameDraft.trim();
      }

      if (
        customerZone.type === "direct" &&
        pendingIds.dray !== (customerZone.DrayDeliveryMethodId ?? null)
      ) {
        updatePayload.DrayDeliveryMethodId = pendingIds.dray;
      }

      if (
        pendingIds.courier !== (customerZone.CourierDeliveryMethodId ?? null)
      ) {
        updatePayload.CourierDeliveryMethodId = pendingIds.courier;
      }

      if (pendingIds.pallet !== (customerZone.PalletDeliveryMethodId ?? null)) {
        updatePayload.PalletDeliveryMethodId = pendingIds.pallet;
      }

      if (
        !isBasicMode &&
        !customerZone.systemDefinedType &&
        deliveryNotesDraft !== null &&
        deliveryNotesDraft !== savedDeliveryNotes
      ) {
        updatePayload.deliveryNotes = deliveryNotesDraft;
      }

      await onSave(updatePayload);
      navigateToParent();
    } catch (error: unknown) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "Unable to save delivery methods. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <NavigationBar
        title={customerZone.name ?? undefined}
        leftIcon="CLOSE"
        leftIconText={isSaving ? "Saving..." : undefined}
        onClickLeftIcon={onClickClose}
        borderBottom={false}
      />
      <div className="mx-auto mt-(--nav-height) flex max-w-230 items-center gap-2">
        <div className="mb-24 flex w-full flex-col gap-4 px-4">
          {saveError && (
            <p role="alert" className="text-red-700">
              {saveError}
            </p>
          )}
          {!isBasicMode && (
            <ZoneNameCard name={nameDraft} onChange={setNameDraft} />
          )}

          <PostcodesCard
            isBasicMode={isBasicMode}
            key={`${deliveryZoneId}:${location.key}`}
            customerZoneId={deliveryZoneId}
            isDefault={customerZone.default}
            systemDefinedType={customerZone.systemDefinedType}
            unbundlingEnabled={unbundlingEnabled}
            onLastPostcodeRemoved={() => {
              setShowNoPostcodesAlert(true);
            }}
          />

          {(!isBasicMode || customerZone.type === "direct") && (
            <DeliverySchedulesCard
              customerZoneId={deliveryZoneId}
              deliveryZoneId={deliveryZoneId}
              unbundlingEnabled={unbundlingEnabled}
            />
          )}
          <AssociatedDeliveryMethodsCard
            zoneType={customerZone.type}
            drayDeliveryMethods={drayDeliveryMethods}
            courierDeliveryMethods={courierDeliveryMethods}
            palletDeliveryMethods={palletDeliveryMethods}
            pendingIds={pendingIds}
            onChange={(next) => {
              const candidate = { ...pendingIds, ...next };
              setPendingIds(candidate);
            }}
            onPreview={(deliveryMethod) => {
              setSelectedDeliveryMethod(deliveryMethod);
              setPreviewSlideOutActive(true);
            }}
          />

          {!isBasicMode && !customerZone.systemDefinedType && (
            <DeliveryNotesCard
              zoneName={customerZone.name}
              value={deliveryNotesDraft ?? savedDeliveryNotes}
              onChange={(next) => setDeliveryNotesDraft(next)}
            />
          )}
          {canRemoveNationwide && (
            <AdminOnly>
              <Card className="flex flex-col items-start gap-4 border border-red-500 bg-white p-4">
                <p>
                  Nationwide delivery is optional. With no nationwide methods,
                  only buyers in your direct or Sellar Distribution areas can
                  discover you. Unsupported customer delivery overrides may be
                  cleared.
                </p>
                <Button
                  color="red"
                  onClick={() => setRemoveNationwideActive(true)}
                >
                  Remove nationwide delivery
                </Button>
              </Card>
            </AdminOnly>
          )}
        </div>
      </div>

      <SingleFieldDialog
        active={removeNationwideActive}
        onDismiss={() => setRemoveNationwideActive(false)}
      >
        <SingleFieldDialogHeader
          title="Remove nationwide delivery?"
          description="Buyers outside your direct and Sellar Distribution areas will no longer discover you. Unsupported customer delivery overrides may be cleared. Placed orders will be kept."
        />
        <SingleFieldDialogCTA>
          <Button
            color="red"
            loading={removeZone.isPending}
            onClick={async () => {
              try {
                await removeZone.mutateAsync();
                navigateToParent();
              } catch {
                setSaveError(
                  "Unable to remove nationwide delivery. Please try again.",
                );
                setRemoveNationwideActive(false);
              }
            }}
          >
            Remove nationwide delivery
          </Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
      <DeliveryMethodPreviewSlideOut
        active={previewSlideOutActive}
        setActive={setPreviewSlideOutActive}
        deliveryMethod={selectedDeliveryMethod}
        deliveryZoneId={deliveryZoneId}
        assign={() => {
          if (!selectedDeliveryMethod) return;
          const fulfillmentType = selectedDeliveryMethod.fulfillmentType;
          const next =
            fulfillmentType === "direct"
              ? { dray: selectedDeliveryMethod.id }
              : fulfillmentType === "courier"
                ? { courier: selectedDeliveryMethod.id }
                : { pallet: selectedDeliveryMethod.id };
          const candidate = { ...pendingIds, ...next };

          setPendingIds(candidate);
          setPreviewSlideOutActive(false);
        }}
      />

      {/* Alert for existing zones with no postcodes */}
      <NoPostcodesAlertDialog
        active={showNoPostcodesAlert}
        onDismiss={() => setShowNoPostcodesAlert(false)}
        zoneId={deliveryZoneId}
        onDeleted={() => navigate(ROUTES.DELIVERY_ZONES)}
      />
    </>
  );
};

const ZoneNameCard = ({
  name,
  onChange,
}: {
  name: string;
  onChange: (next: string) => void;
}) => {
  return (
    <Card className="bg-white">
      <CardContent className="flex flex-col gap-1 p-4">
        <InputLabel>Name</InputLabel>
        <InputContainer className="relative flex w-full items-center gap-1">
          <InputText value={name} onChange={(e) => onChange(e.target.value)} />
        </InputContainer>
      </CardContent>
    </Card>
  );
};

const PostcodesCard = ({
  isBasicMode,
  customerZoneId,
  isDefault,
  systemDefinedType,
  unbundlingEnabled,
  onLastPostcodeRemoved,
}: {
  isBasicMode: boolean;
  customerZoneId: number;
  isDefault: boolean;
  systemDefinedType: CustomerZoneGetResponseType["systemDefinedType"];
  unbundlingEnabled: boolean;
  onLastPostcodeRemoved?: () => void;
}) => {
  const uniqueDirectPostcodes = useUniqueDirectPostcodes();
  const [isEditingPostcodes, setIsEditingPostcodes] = useState(false);
  const [postcodeDraft, setPostcodeDraft] = useState("");

  const [invalidPostcode, setInvalidPostcode] = useState(false);
  const [postcodeAddError, setPostcodeAddError] = useState<string | null>(null);
  const [scheduleDialogPostcode, setScheduleDialogPostcode] = useState<
    string | null
  >(null);
  const [postcodeDeleteTarget, setPostcodeDeleteTarget] = useState<
    string | null
  >(null);
  const [moveDialogActive, setMoveDialogActive] = useState(false);
  const [pendingPostcodes, setPendingPostcodes] = useState<string[]>([]);
  const [emptiedZone, setEmptiedZone] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const customerZonePostcodesQuery = useCustomerZonePostcodes(customerZoneId, {
    enabled: unbundlingEnabled && !isDefault && systemDefinedType === null,
  });
  const addPostcodesMutation = useCustomerZonePostcodesAdd(customerZoneId);
  const removePostcodesMutation =
    useCustomerZonePostcodesRemove(customerZoneId);

  const movePostcodesMutation = useCustomerZonePostcodesMove(customerZoneId);
  const deleteEmptiedZoneMutation = useCustomerZonesDelete(
    emptiedZone?.id ?? 0,
  );

  const deliveryAreasQuery = useDeliveryAreaList(
    {
      limit: 50,
    },
    {
      enabled: unbundlingEnabled,
    },
  );

  useEffect(() => {
    if (!unbundlingEnabled) return;
    if (!deliveryAreasQuery.hasNextPage) return;
    if (deliveryAreasQuery.isFetchingNextPage) return;

    deliveryAreasQuery.fetchNextPage();
  }, [
    deliveryAreasQuery,
    deliveryAreasQuery.hasNextPage,
    deliveryAreasQuery.isFetchingNextPage,
    unbundlingEnabled,
  ]);

  const deliveryAreaPostcodeComponents =
    deliveryAreasQuery.data?.pages
      .flat()
      .filter((da) => da.systemDefinedType === null)
      .flatMap((da) => da.DeliveryAreaPostcodes)
      .map((p) => p.postcodeComponent)
      .filter((pc): pc is string => !!pc) ?? [];

  const customerZonePostcodeComponents =
    customerZonePostcodesQuery.data?.map((p) => p.postcodeComponent) ?? [];

  const customerZonePostcodesNotCoveredByDeliveryAreas =
    unbundlingEnabled &&
    systemDefinedType === null &&
    customerZonePostcodeComponents.length
      ? deliveryAreaPostcodeComponents.length
        ? getCoverageMismatches({
            leftPostcodeComponents: customerZonePostcodeComponents,
            rightPostcodeComponents: deliveryAreaPostcodeComponents,
          }).leftNotCoveredByRight
        : customerZonePostcodeComponents
      : [];

  const deliveryAreas =
    deliveryAreasQuery.data?.pages
      .flat()
      .filter((da) => da.systemDefinedType === null) ?? [];

  return (
    <Card className="bg-white">
      <CardHeader className="flex flex-col items-start gap-1 bg-white p-4">
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="font-semibold">Postcodes</span>
          {!isBasicMode &&
            unbundlingEnabled &&
            !isDefault &&
            systemDefinedType === null && (
              <Button
                color="secondary"
                disabled={
                  customerZonePostcodesQuery.isLoading ||
                  addPostcodesMutation.isPending ||
                  removePostcodesMutation.isPending ||
                  movePostcodesMutation.isPending
                }
                onClick={() => {
                  setIsEditingPostcodes((editing) => !editing);
                  setPostcodeDraft("");
                  setInvalidPostcode(false);
                  setPostcodeAddError(null);
                }}
              >
                {isEditingPostcodes ? "Done editing" : "Edit postcodes"}
              </Button>
            )}
        </div>
        {isBasicMode ? (
          <>
            <p>
              {isDefault
                ? "Nationwide covers all other postcodes outside Local."
                : "Any customer whose postcode is listed below, will fall into this delivery zone"}
            </p>
            {!isDefault && (
              <Alert
                type="information"
                message={
                  <strong>
                    The postcodes listed in your direct delivery schedules make
                    up your Local default delivery zone
                  </strong>
                }
                className="w-full px-3 py-1"
              />
            )}
          </>
        ) : (
          <p>
            Any customers whose postcode is described below, will fall into this
            delivery zone
            <br />
            <br />
            <Anchor
              className="inline-flex items-center gap-1 text-blue-600 underline"
              href="https://intercom.help/sellario/en/articles/9981916-supplier-guide-delivery-settings#h_d37178a34b"
              target="_blank"
              rel="noopener noreferrer"
            >
              Where have these postcodes come from?
              <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
            </Anchor>
          </p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-1 p-4">
        {!deliveryAreasQuery.isPending &&
          !deliveryAreasQuery.isError &&
          !deliveryAreasQuery.hasNextPage &&
          !!customerZonePostcodesNotCoveredByDeliveryAreas.length && (
            <Alert
              type={"information"}
              title={`${customerZonePostcodesNotCoveredByDeliveryAreas[0]} is not listed in a delivery schedule`}
              message={
                "This means customers in these postcodes will see your chosen direct delivery option but won't be shown any estimated delivery date predictions with it"
              }
              className="w-full"
            >
              {deliveryAreas.length > 0 && (
                <Button
                  color="secondary"
                  className="border border-blue-700 bg-transparent text-blue-700"
                  onClick={() => {
                    setScheduleDialogPostcode(
                      customerZonePostcodesNotCoveredByDeliveryAreas[0] ?? null,
                    );
                  }}
                >
                  Add to a delivery schedule
                </Button>
              )}
            </Alert>
          )}
        {isDefault ? (
          isBasicMode ? (
            "All other postcodes"
          ) : (
            "All postcodes"
          )
        ) : systemDefinedType ? (
          uniqueDirectPostcodes.tagList
        ) : !unbundlingEnabled ? (
          uniqueDirectPostcodes.tagList
        ) : !isEditingPostcodes ? (
          customerZonePostcodesQuery.isLoading ? (
            <p>Loading postcodes…</p>
          ) : customerZonePostcodesQuery.isError ? (
            <p role="alert">Unable to load postcodes. Please try again.</p>
          ) : customerZonePostcodeComponents.length === 0 ? (
            <p>
              {isBasicMode
                ? "Enter postcodes in direct delivery schedules to set up Local coverage."
                : "No postcodes added"}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {groupPostcodes(customerZonePostcodeComponents).map((group) => (
                <li key={group.area}>{group.postcodes.join(", ")}</li>
              ))}
            </ul>
          )
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-600">
              Changes are saved automatically.
            </p>
            <div className="flex gap-4">
              <InputContainer className="w-full">
                <InputText
                  className="w-full"
                  value={postcodeDraft}
                  onChange={(e) => {
                    setPostcodeAddError(null);
                    setPostcodeDraft(e.target.value);
                  }}
                  placeholder="e.g. BS1 or S1-9"
                />
              </InputContainer>
              <Button
                loading={addPostcodesMutation.isPending}
                disabled={addPostcodesMutation.isPending}
                onClick={() => {
                  setInvalidPostcode(false);
                  setPostcodeAddError(null);
                  const postcode = postcodeDraft;
                  if (!postcode) return;

                  const expandedPostcodes = expandPostcodeRange(postcode);

                  if (
                    expandedPostcodes.some(
                      (pc) => !checkPostcodeValidCharacters(pc),
                    )
                  ) {
                    return setInvalidPostcode(true);
                  }

                  const postcodesToAdd = expandedPostcodes.map((pc) =>
                    pc.toUpperCase(),
                  );

                  addPostcodesMutation.mutate(
                    {
                      postcodeComponents: postcodesToAdd,
                    },
                    {
                      onSuccess: (
                        response: CustomerZonePostcodeAddResponseType,
                      ) => {
                        setPostcodeDraft("");

                        const firstUncovered = postcodesToAdd
                          .map((pc) => normalizePostcodeComponent(pc))
                          .find((normalized) => {
                            const coverage = response.coverage.find(
                              (c) => c.postcodeComponent === normalized,
                            );
                            return (
                              coverage && !coverage.isCoveredByDeliverySchedule
                            );
                          });

                        if (firstUncovered && deliveryAreas.length > 0) {
                          setScheduleDialogPostcode(firstUncovered);
                        }
                      },
                      onError: (error) => {
                        if (isPostcodeConflictError(error)) {
                          setPendingPostcodes(postcodesToAdd);
                          setMoveDialogActive(true);
                          return;
                        }

                        const apiError = apiV2AssertError(error);
                        const message = apiError?.response?.data.message ?? "";
                        setPostcodeAddError(
                          message || "Unable to add postcode",
                        );
                      },
                    },
                  );
                }}
              >
                <Icon icon={"PLUS"} />
                Add
              </Button>
            </div>
            {postcodeAddError && (
              <p className="text-sm text-red-700">{postcodeAddError}</p>
            )}
            {invalidPostcode && (
              <Alert
                title={"Unable to save postcode"}
                message={
                  "Please enter either a postcode area (such as BS), postcode district (such as BS1) or postcode sector (such as BS1 6)."
                }
              />
            )}
            <div className="flex flex-wrap gap-2">
              {customerZonePostcodeComponents.map((postcode) => (
                <DataButton
                  key={postcode}
                  disabled={removePostcodesMutation.isPending}
                  border={true}
                  className={twMerge(
                    customerZonePostcodesNotCoveredByDeliveryAreas.includes(
                      postcode,
                    ) && "border-blue-800 bg-blue-100 text-blue-800",
                  )}
                  onClick={() => {
                    if (
                      customerZonePostcodeComponents.length === 1 &&
                      onLastPostcodeRemoved
                    ) {
                      onLastPostcodeRemoved();
                    } else if (
                      customerZonePostcodesNotCoveredByDeliveryAreas.includes(
                        postcode,
                      )
                    ) {
                      // Postcode is not covered by any delivery schedule - delete immediately without modal
                      removePostcodesMutation.mutate({
                        postcodeComponents: [postcode],
                      });
                    } else {
                      // Postcode is covered by a delivery schedule - show the modal
                      setPostcodeDeleteTarget(postcode);
                    }
                  }}
                >
                  <span className="flex items-center gap-2">
                    {customerZonePostcodesNotCoveredByDeliveryAreas.includes(
                      postcode,
                    ) ? (
                      <Icon icon="INFORMATION_OUTLINE" size={0.6} />
                    ) : (
                      ""
                    )}
                    {postcode}
                    <Icon icon="CLOSE" size={0.6} />
                  </span>
                </DataButton>
              ))}
            </div>

            <PostcodeRemoveDialog
              customerZoneId={customerZoneId}
              postcodeComponent={postcodeDeleteTarget}
              onDismiss={() => setPostcodeDeleteTarget(null)}
            />

            <PostcodeMoveDialog
              active={moveDialogActive}
              postcodeCount={pendingPostcodes.length}
              onDismiss={() => {
                setMoveDialogActive(false);
                setPendingPostcodes([]);
              }}
              onMove={async () => {
                try {
                  const result = await movePostcodesMutation.mutateAsync({
                    postcodeComponents: pendingPostcodes,
                  });
                  setMoveDialogActive(false);
                  setPendingPostcodes([]);
                  setPostcodeDraft("");
                  // Check if any zones were emptied and show dialog for the first one
                  const firstEmptiedZone = result.emptiedZones?.[0];
                  if (firstEmptiedZone) {
                    setEmptiedZone(firstEmptiedZone);
                  }
                } catch (error) {
                  const apiError = apiV2AssertError(error);
                  setPostcodeAddError(
                    apiError?.response?.data.message ??
                      "Failed to move postcodes",
                  );
                }
              }}
              isMoving={movePostcodesMutation.isPending}
            />

            <EmptyZoneAfterMoveDialog
              active={emptiedZone !== null}
              emptiedZone={emptiedZone}
              onDismiss={() => setEmptiedZone(null)}
              onDelete={() => {
                deleteEmptiedZoneMutation.mutate(undefined, {
                  onSuccess: () => setEmptiedZone(null),
                });
              }}
              isDeleting={deleteEmptiedZoneMutation.isPending}
            />
          </div>
        )}
        <DeliveryScheduleDialog
          active={scheduleDialogPostcode !== null}
          postcode={scheduleDialogPostcode}
          deliveryAreas={deliveryAreas}
          onDismiss={() => setScheduleDialogPostcode(null)}
        />
      </CardContent>
    </Card>
  );
};

const DeliverySchedulesCard = ({
  customerZoneId,
  deliveryZoneId,
  unbundlingEnabled,
  disabled = false,
}: {
  customerZoneId: number;
  deliveryZoneId: number | undefined;
  unbundlingEnabled: boolean;
  disabled?: boolean;
}) => {
  const navigate = useNavigate();

  const deliveryAreasQuery = useCustomerZoneDeliveryAreas(customerZoneId, {
    enabled: unbundlingEnabled && !!deliveryZoneId,
  });

  const deliveryAreas = deliveryAreasQuery.data ?? [];

  return (
    <Card
      className={twMerge(
        "bg-white",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <CardHeader className="flex flex-col items-start gap-1 bg-white p-4">
        <span className="font-semibold">Delivery schedules</span>
        <p>Any linked delivery schedules are listed below</p>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2 p-4">
        {deliveryAreas.map((area) => (
          <DataButton
            key={area.id}
            border={true}
            onClick={() =>
              navigate(
                ROUTES.DELIVERY_SCHEDULE.href({ deliveryAreaId: area.id }),
                {
                  state: {
                    fromCustomerZoneId: deliveryZoneId,
                  },
                },
              )
            }
          >
            <span className="flex items-center gap-1 font-normal">
              {area.name}
              <Icon icon="CHEVRON_RIGHT" size={0.6} />
            </span>
          </DataButton>
        ))}
      </CardContent>
    </Card>
  );
};

const PostcodeRemoveDialog = (props: {
  customerZoneId: number;
  postcodeComponent: string | null;
  onDismiss: () => void;
}) => (
  <PostcodeRemoveDialogContent
    key={props.postcodeComponent ?? "closed"}
    {...props}
  />
);

const PostcodeRemoveDialogContent = ({
  customerZoneId,
  postcodeComponent,
  onDismiss,
}: {
  customerZoneId: number;
  postcodeComponent: string | null;
  onDismiss: () => void;
}) => {
  const removePostcodesMutation =
    useCustomerZonePostcodesRemove(customerZoneId);

  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <SingleFieldDialog
      active={postcodeComponent !== null}
      onDismiss={onDismiss}
      size="large"
    >
      <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/app-assets/delivery-zone-delete-generic.png" />
      <SingleFieldDialogHeader
        title={`Are you sure you want to remove ${postcodeComponent} from this delivery zone?`}
        description={
          <>
            Any new customers based in {postcodeComponent} that come to your
            storefront and any current customers in {postcodeComponent}{" "}
            inheriting their delivery methods from this delivery zone will see
            your default delivery settings.
          </>
        }
        icon="DELETE"
        iconClassName="bg-red-50 text-red-600"
      />

      {error && <Alert type="error" message={error} />}

      <SingleFieldDialogCTA className="items-center">
        <label className="mr-auto ml-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I confirm I want to remove this postcode
        </label>

        <Button color="transparent" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button
          color="red"
          loading={removePostcodesMutation.isPending}
          disabled={
            !postcodeComponent || !checked || removePostcodesMutation.isPending
          }
          onClick={() => {
            if (!postcodeComponent) return;

            setError(null);
            removePostcodesMutation.mutate(
              {
                postcodeComponents: [postcodeComponent],
              },
              {
                onSuccess: onDismiss,
                onError: (e) => {
                  const assertedError = apiV2AssertError(e);
                  setError(
                    assertedError?.message ??
                      "Unable to remove postcode from delivery zone",
                  );
                },
              },
            );
          }}
        >
          Remove
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const NoPostcodesAlertDialog = ({
  active,
  onDismiss,
  zoneId,
  onDeleted,
}: {
  active: boolean;
  onDismiss: () => void;
  zoneId: number | undefined;
  onDeleted: () => void;
}) => {
  const deleteZoneMutation = useCustomerZonesDelete(zoneId ?? 0);

  return (
    <SingleFieldDialog active={active} onDismiss={onDismiss}>
      <SingleFieldDialogHeader
        title="Delivery zone has no postcodes"
        description="You cannot save a delivery zone without any postcodes defined."
      />
      <SingleFieldDialogCTA>
        <Button color="secondary" onClick={onDismiss}>
          Continue editing
        </Button>
        <Button
          color="red"
          loading={deleteZoneMutation.isPending}
          disabled={!zoneId || deleteZoneMutation.isPending}
          onClick={() => {
            if (!zoneId) return;
            deleteZoneMutation.mutate(undefined, {
              onSuccess: onDeleted,
            });
          }}
        >
          Delete zone & exit
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const DeliveryScheduleDialog = (props: {
  active: boolean;
  postcode: string | null;
  deliveryAreas: DeliveryAreaListResponseItemType[];
  onDismiss: () => void;
}) => (
  <DeliveryScheduleDialogContent
    key={`${props.active}:${props.postcode ?? ""}`}
    {...props}
  />
);

const DeliveryScheduleDialogContent = ({
  active,
  postcode,
  deliveryAreas,
  onDismiss,
}: {
  active: boolean;
  postcode: string | null;
  deliveryAreas: DeliveryAreaListResponseItemType[];
  onDismiss: () => void;
}) => {
  const navigate = useNavigate();
  const [selectedDeliveryAreaIds, setSelectedDeliveryAreaIds] = useState<
    number[]
  >([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const deliveryAreaUpdateMutationsRef = useRef<DeliveryAreaUpdateMutationsMap>(
    {},
  );

  const onConfirm = async () => {
    const postcodeToAdd = postcode?.trim().toUpperCase();
    if (!postcodeToAdd) return;
    if (selectedDeliveryAreaIds.length === 0) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      for (const deliveryAreaId of selectedDeliveryAreaIds) {
        const deliveryArea = deliveryAreas.find(
          (da) => da.id === deliveryAreaId,
        );
        if (!deliveryArea) continue;

        const deliveryAreaUpdateMutation =
          deliveryAreaUpdateMutationsRef.current[deliveryAreaId];
        if (!deliveryAreaUpdateMutation) continue;

        const existingPostcodes = deliveryArea.DeliveryAreaPostcodes.map(
          (p) => p.postcodeComponent,
        ).filter((pc): pc is string => !!pc);

        const nextPostcodes = [
          ...new Set([...existingPostcodes, postcodeToAdd]),
        ];

        await deliveryAreaUpdateMutation.mutateAsync({
          startAt: deliveryArea.startAt,
          name: deliveryArea.name,
          weeklyCadence: deliveryArea.weeklyCadence,
          orderCutOffTime: deliveryArea.orderCutOffTime,
          orderCutOffDaysBefore: deliveryArea.orderCutOffDaysBefore,
          includeWeekend: deliveryArea.includeWeekend,
          days: deliveryArea.DeliveryAreaDays.map((d) => d.day),
          postcodes: nextPostcodes,
        });
      }

      onDismiss();
    } catch {
      setSaveError("Unable to add postcode to delivery schedule");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog active={active} onDismiss={onDismiss} fullHeight>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={`The postcode ${postcode ?? ""} is not currently listed in a delivery schedule`}
        description={
          "Choose one or more delivery schedules from the list below so that the customers in this postcode see delivery date information alongside their direct delivery method."
        }
      />
      <DialogContent className="flex flex-col gap-2">
        {deliveryAreas.length === 0 ? (
          <Card className="flex flex-col items-center gap-4 border border-zinc-300 bg-white p-8 text-center">
            <Icon icon="MAP_CLOCK" size={2} className="text-zinc-400" />
            <div>
              <p className="font-semibold text-zinc-800">
                No delivery schedules available
              </p>
              <p className="text-sm text-zinc-500">
                You need to create a delivery schedule before you can add this
                postcode
              </p>
            </div>
          </Card>
        ) : (
          <>
            <Card className="flex flex-col gap-4 border border-zinc-300 bg-white p-4">
              {deliveryAreas.map((deliveryArea) => (
                <DeliveryScheduleDialogOption
                  key={deliveryArea.id}
                  deliveryArea={deliveryArea}
                  isSelected={selectedDeliveryAreaIds.includes(deliveryArea.id)}
                  onToggle={() => {
                    setSaveError(null);
                    setSelectedDeliveryAreaIds((current) =>
                      current.includes(deliveryArea.id)
                        ? current.filter((id) => id !== deliveryArea.id)
                        : [...current, deliveryArea.id],
                    );
                  }}
                  deliveryAreaUpdateMutationsRef={
                    deliveryAreaUpdateMutationsRef
                  }
                />
              ))}
            </Card>

            <p className="text-zinc-500">
              <b>Note:</b> You can choose more than one delivery schedule if you
              pass through this postcode on more than one delivery route
            </p>

            {saveError && <Alert type={"error"} message={saveError} />}
          </>
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          color="secondary"
          onClick={() => navigate(ROUTES.DELIVERY_SCHEDULES)}
        >
          Go to delivery schedules
        </Button>
        <Button
          disabled={
            deliveryAreas.length > 0 &&
            (isSaving ||
              selectedDeliveryAreaIds.length === 0 ||
              postcode === null)
          }
          onClick={deliveryAreas.length === 0 ? onDismiss : onConfirm}
        >
          {deliveryAreas.length === 0 ? "Dismiss" : "Confirm"}
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const DeliveryScheduleDialogOption = ({
  deliveryArea,
  isSelected,
  onToggle,
  deliveryAreaUpdateMutationsRef,
}: {
  deliveryArea: DeliveryAreaListResponseItemType;
  isSelected: boolean;
  onToggle: () => void;
  deliveryAreaUpdateMutationsRef: { current: DeliveryAreaUpdateMutationsMap };
}) => {
  const deliveryAreaUpdateMutation = useDeliveryAreaUpdate(deliveryArea.id);

  useEffect(() => {
    const currentMutations = deliveryAreaUpdateMutationsRef.current;

    currentMutations[deliveryArea.id] = deliveryAreaUpdateMutation;

    return () => {
      delete currentMutations[deliveryArea.id];
    };
  }, [
    deliveryArea.id,
    deliveryAreaUpdateMutation,
    deliveryAreaUpdateMutationsRef,
  ]);

  const days = deliveryArea.DeliveryAreaDays.map((d) => d.day);
  const cadenceLabel = deliveryArea.weeklyCadence
    ? `Every ${deliveryArea.weeklyCadence} week${deliveryArea.weeklyCadence === 1 ? "" : "s"}`
    : "No cadence";
  const daysLabel = days.length
    ? `on ${days.map((d) => capitalize(d)).join(", ")}`
    : "";

  return (
    <button
      type="button"
      className="flex w-full cursor-pointer gap-3"
      onClick={onToggle}
    >
      <ListItemIconTile icon={isSelected ? "CHECK" : "MAP_CLOCK"} />
      <div className="flex flex-col items-start">
        <div className="font-semibold">{deliveryArea.name}</div>
        <div className="text-xs text-zinc-600">
          {cadenceLabel} {daysLabel}
        </div>
      </div>
    </button>
  );
};

const getSubtitle = (
  deliveryMethod: FulfillmentTypeListResponseType[number],
) => {
  const noOrderRules = "No order rules";
  const minimumOrderPrice = deliveryMethod.OrderRules?.minimumOrderPrice
    ? `${formatCurrency(deliveryMethod.OrderRules.minimumOrderPrice)} minimum`
    : null;

  const nbOfOrderRules =
    deliveryMethod.OrderRules?.PriceTierOrderRules.length ?? 0;
  const orderRulesCopy =
    nbOfOrderRules > 0
      ? `${nbOfOrderRules} container order ${pluralize("rule", nbOfOrderRules)}`
      : null;
  const orderRules = [minimumOrderPrice, orderRulesCopy]
    .filter(Boolean)
    .join(" and ");

  const flatCharge = deliveryMethod.charge
    ? `${formatCurrency(deliveryMethod.charge)} flat delivery charge`
    : null;

  const nbContainerDeliveryCharges =
    deliveryMethod.FulfillmentTypeContainerDeliveryCharges.length;
  const containerCharges =
    nbContainerDeliveryCharges > 0
      ? `${nbContainerDeliveryCharges} container delivery ${pluralize(
          "charge",
          nbContainerDeliveryCharges,
        )}`
      : null;

  const nbContainerRules = deliveryMethod.DeliveryMethodContainerRules.length;
  const containerRules =
    nbContainerRules > 0
      ? `${nbContainerRules} container ${pluralize(
          "type",
          nbContainerRules,
        )} not available`
      : null;

  return [
    ...(!!orderRules ? [orderRules] : [noOrderRules]),
    flatCharge,
    containerCharges,
    containerRules,
  ]
    .filter(Boolean)
    .join(", ");
};

const AssociatedDeliveryMethodsCard = ({
  zoneType,
  drayDeliveryMethods,
  courierDeliveryMethods,
  palletDeliveryMethods,
  pendingIds,
  onChange,
  onPreview,
  disabled = false,
}: {
  zoneType: CustomerZoneGetResponseType["type"];
  drayDeliveryMethods: FulfillmentTypeListResponseType[number][];
  courierDeliveryMethods: FulfillmentTypeListResponseType[number][];
  palletDeliveryMethods: FulfillmentTypeListResponseType[number][];
  pendingIds: {
    dray: number | null;
    courier: number | null;
    pallet: number | null;
  };
  onChange: (
    next: Partial<{
      dray: number | null;
      courier: number | null;
      pallet: number | null;
    }>,
  ) => void;
  onPreview: (deliveryMethod: FulfillmentTypeListResponseType[number]) => void;
  disabled?: boolean;
}) => {
  return (
    <Card
      className={twMerge(
        "bg-white",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <CardHeader className="flex flex-col items-start gap-1 bg-white p-4">
        <span className="font-semibold">Associated delivery methods</span>
        <p>
          Choose which delivery methods are available to customers that fall
          into this delivery zone
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4">
        {zoneType === "direct" && (
          <div className="flex flex-col gap-2">
            <Alert
              type="information"
              title="Only select a direct delivery method if you will be servicing this delivery zone with your own vehicles"
              message=""
            />
            <DeliveryMethodsRadioGroup
              title="Select direct"
              options={drayDeliveryMethods}
              value={pendingIds.dray}
              getSubtitle={getSubtitle}
              onChange={(next) => onChange({ dray: next })}
              onPreview={onPreview}
            />
          </div>
        )}

        <DeliveryMethodsRadioGroup
          title="Select courier"
          options={courierDeliveryMethods}
          value={pendingIds.courier}
          getSubtitle={getSubtitle}
          onChange={(next) => onChange({ courier: next })}
          onPreview={onPreview}
        />

        <DeliveryMethodsRadioGroup
          title="Select pallet"
          options={palletDeliveryMethods}
          value={pendingIds.pallet}
          getSubtitle={getSubtitle}
          onChange={(next) => onChange({ pallet: next })}
          onPreview={onPreview}
        />
      </CardContent>
    </Card>
  );
};

const DeliveryNotesCard = ({
  zoneName,
  value,
  onChange,
  disabled = false,
}: {
  zoneName: string | null;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) => {
  return (
    <Card
      className={twMerge(
        "bg-white",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <CardHeader className="flex flex-col items-start gap-1 bg-white p-4">
        <span className="font-semibold">Delivery notes</span>
        <p>Shown to all {zoneName} customers; new and existing</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="flex flex-col items-end gap-2">
          <InputContainer className="w-full">
            <InputTextArea
              variant="legacyFixed"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-22.5"
            />
          </InputContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const DeliveryMethodsRadioGroup = ({
  title,
  options,
  value,
  onChange,
  getSubtitle,
  onPreview,
}: {
  title: string;
  options: FulfillmentTypeListResponseType[number][];
  value: number | null;
  onChange: (next: number | null) => void;
  getSubtitle: (
    deliveryMethod: FulfillmentTypeListResponseType[number],
  ) => string | undefined;
  onPreview: (deliveryMethod: FulfillmentTypeListResponseType[number]) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-semibold text-zinc-800">{title}</span>
      <RadioGroup
        value={value !== null ? String(value) : ""}
        onChange={(next) => onChange(Number(next))}
      >
        <RadioGroupContainer>
          {options.map((method) => (
            <RadioGroupOption
              key={method.id}
              value={String(method.id)}
              onClick={({ checked, event }) => {
                if (checked) {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange(null);
                }
              }}
            >
              <div className="flex flex-1 items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {method.name ??
                      `${capitalize(method.fulfillmentType)} delivery`}
                  </span>
                  <span className="text-sm text-zinc-500">
                    {getSubtitle(method)}
                  </span>
                </div>
                <DataButton
                  border={true}
                  className="ml-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPreview(method);
                  }}
                >
                  <Icon icon="EYE" size={0.65} />
                  Preview
                </DataButton>
              </div>
            </RadioGroupOption>
          ))}
        </RadioGroupContainer>
      </RadioGroup>
    </div>
  );
};
